import { api, API_BASE_URL } from '@shared/api';

/** «Истории» (2026-10-22): история с ИИ-ведущим, задание дня и босс из ошибок — один формат прохождения (`mode`). */

export type RunMode = 'story' | 'daily' | 'boss';
export type RunStatus = 'active' | 'finished' | 'abandoned' | 'failed';
export type ReplySource = 'text' | 'voice';
/** Как у ученика с английским (спрашивает игра, 2026-10-25): zero «С нуля», basic «Немного», confident «Уверенно». */
export type LearnerLevel = 'zero' | 'basic' | 'confident';

export interface StoryLine {
  text: string;
  /** null — ElevenLabs не озвучил: читаем голосом браузера. */
  audio: string | null;
  /** Перевод реплики (по нажатию); пусто — перевода нет. */
  ru?: string;
}

export interface StoryHint {
  tactic: string;
  text: string;
  ru?: string;
}

export interface StoryWord {
  en: string;
  ru: string;
}

export interface StoryScene {
  location: string;
  speaker: string;
  speaker_name: string;
  speaker_role: string;
  speaker_gender: 'f' | 'm';
  narration_ru: string;
  lines: StoryLine[];
  event: { id: string; title_ru: string; text_ru: string } | null;
  hints: StoryHint[];
  words: StoryWord[];
}

/** Урок грамматики к ошибке — только открытый ученикам; `cheat` — строки «Запомни» из шагов урока (2026-10-25). */
export interface StoryLesson {
  slug: string;
  title: string;
  cheat?: string[];
}

export interface StoryFix {
  wrong: string;
  right: string;
  rule_ru: string;
  why_ru: string;
  topic?: string | null;
  lesson?: StoryLesson | null;
}

export interface StoryPron {
  heard: string;
  meant: string;
  tip_ru: string;
}

export interface StoryFeedback {
  text: string;
  source: ReplySource;
  understood: boolean;
  answered: boolean;
  tone: 'polite' | 'neutral' | 'rude';
  fixes: StoryFix[];
  corrected: string;
  pron: StoryPron[];
  effects: Partial<Record<'money' | 'minutes' | 'battery' | 'trust', number>>;
  fallback: boolean;
  /** Задание дня: шаги, выполненные к этому ходу. */
  steps_done?: string[];
  /** Босс: попал ли ученик, правильный вариант и что было исправить. */
  ok?: boolean;
  sentence?: string;
  right?: string;
  focus?: { wrong: string; right: string; rule: string; why: string }[];
  praise_ru?: string;
  tip_ru?: string;
}

export interface StoryMistake {
  id: string;
  run_id: string;
  turn: number;
  kind: 'grammar' | 'pron';
  wrong: string;
  right: string;
  rule: string;
  why: string;
  sentence: string;
  corrected: string;
  resolved_at: string | null;
  repeats: number;
  created_at: string | null;
  topic?: string | null;
  lesson?: StoryLesson | null;
}

/** Цены игр в жетонах (решение владельца 2026-10-22, вместо лимитов «историй в неделю» и «боёв в день»). */
export interface StoryPrices {
  story: number;
  daily: number;
  boss: number;
}

export interface StoryEnding {
  id: string;
  title: string;
  text: string;
  stars: number;
  secret: boolean;
  new: boolean;
  stamps: { id: string; title: string; new: boolean }[];
  words: StoryWord[];
  mistakes: StoryMistake[];
  stats: { turns: number; fixes: number; voice_turns: number; polite_turns: number };
}

export interface DailyResult {
  done: boolean;
  stars: number;
  steps_done: number;
  steps_total: number;
  words: StoryWord[];
  mistakes: StoryMistake[];
  stats: { turns: number; fixes: number; voice_turns: number };
  next_at: string;
  /** Сколько несгораемых жетонов начислено за эту миссию; null — не выполнена (2026-10-22). */
  reward: number | null;
}

export interface BossResult {
  win: boolean;
  stars: number;
  rounds: { sentence: string; right: string; answer: string; ok: boolean; mine: boolean; rule: string }[];
  resolved: number;
  open_left: number;
}

export interface StoryRun {
  id: string;
  mode: RunMode;
  template_id: string;
  title: string;
  goal: string;
  status: RunStatus;
  turn: number;
  max_turns: number;
  /** Цена нового прохождения этого режима — для «Сыграть ещё» на итогах. */
  price_tokens: number;
  scene: StoryScene;
  /** «Ход разговора»: у ответа — его правки для окна «Поправки к ответу» (2026-10-25). */
  history: {
    turn: number;
    speaker: string;
    lines: string[];
    reply: string | null;
    source: ReplySource | null;
    fixes: number;
    lines_ru?: string[];
    corrected?: string;
    corrections?: StoryFix[];
    pron?: StoryPron[];
  }[];
  resources?: {
    money: number;
    battery: number;
    trust: number;
    minutes: number;
    clock: string;
    deadline: string;
    minutes_left: number;
  };
  mission?: { id: string; location: string; steps: { id: string; title: string; done: boolean }[] };
  boss?: {
    name: string;
    hp: number;
    max_hp: number;
    lives: number;
    max_lives: number;
    round: number;
    rounds: number;
    current: { sentence: string; mine: boolean; hint: string } | null;
  };
  ending: StoryEnding | null;
  result: DailyResult | BossResult | null;
  /** Уровень ученика: null — игра ещё не спрашивала (у вошедших), в демо — выбор на стартовом экране. */
  learner_level?: LearnerLevel | null;
  /** Демо без регистрации (/demo, 2026-10-25): ответов осталось, когда исчезнет, награды миссии дня для призыва. */
  demo?: { turns_left: number; max_turns: number; expires_at: string | null; reward: { done: number; perfect: number } };
}

/** Стартовый экран демо: миссия и можно ли сыграть сегодня (closed — кончились у всех, limit — с адреса уже сыграно). */
export interface DemoInfo {
  title: string;
  goal: string;
  narration: string;
  character: string;
  role: string;
  location: string;
  max_turns: number;
  steps: { id: string; title: string }[];
  available: boolean;
  reason: 'closed' | 'limit' | null;
}

export interface StoryCard {
  id: string;
  title: string;
  tagline: string;
  goal: string;
  max_turns: number;
  active_run_id: string | null;
  finished_runs: number;
  best_stars: number | null;
  endings: { id: string; title: string | null; secret: boolean; unlocked: boolean }[];
  stamps: { id: string; title: string; hint: string; unlocked: boolean }[];
  next_chapter: { id: string; title: string; tagline: string; interest: 'yes' | 'like' | null } | null;
}

export interface DailyCard {
  id: string;
  title: string;
  goal: string;
  location: string;
  character: string;
  max_turns: number;
  steps: { id: string; title: string; done: boolean }[];
  run_id: string | null;
  status: RunStatus | null;
  stars: number | null;
  done: boolean;
  resets_at: string;
  /** Награда несгораемыми жетонами: выполнена — `done`, на три звезды — `perfect` (решение владельца 2026-10-22). */
  reward: { done: number; perfect: number };
  /** Начислено за сегодняшнюю миссию; null — ещё ничего. */
  granted: number | null;
  /** Начислено этим запросом: миссия была пройдена без начисления (до появления наград). */
  just_granted: number | null;
}

export interface BossCard {
  name: string;
  title: string;
  active_run_id: string | null;
  open_mistakes: number;
  resolved_mistakes: number;
  wins: number;
}

export interface StoriesHub {
  stories: StoryCard[];
  prices: StoryPrices;
  mistakes: number;
  daily: DailyCard;
  boss: BossCard;
}

/** Начисление несгораемых жетонов в ответе хода: за миссию дня (`reason: 'mission'`). */
export interface StoryBonusGrant {
  amount: number;
  reason: string;
  stars?: number;
}

export interface TurnResponse {
  run: StoryRun;
  feedback: StoryFeedback;
  transcript?: string;
  bonus_granted?: StoryBonusGrant[];
}

/** Адрес озвучки: бэк отдаёт путь от корня API-хоста (`/api/v1/stories/audio/…`). */
export function audioSrc(path: string | null): string | null {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE_URL.replace(/\/api\/v1$/, '')}${path}`;
}

/** GET /stories — истории с альбомом, задание дня, босс, лимит и число ошибок. */
export async function fetchStoriesHub(): Promise<StoriesHub> {
  const { data } = await api.get<StoriesHub>('/stories');
  return data;
}

/** POST /stories/{id}/runs — новое прохождение (списывает лимит) или активное. */
export async function startStory(templateId: string, restart = false): Promise<StoryRun> {
  const { data } = await api.post<StoryRun>(`/stories/${templateId}/runs`, { restart });
  return data;
}

export async function startDaily(): Promise<StoryRun> {
  const { data } = await api.post<StoryRun>('/stories/daily/runs');
  return data;
}

export async function startBoss(): Promise<StoryRun> {
  const { data } = await api.post<StoryRun>('/stories/boss/runs');
  return data;
}

export async function fetchRun(runId: string): Promise<StoryRun> {
  const { data } = await api.get<StoryRun>(`/stories/runs/${runId}`);
  return data;
}

/** Ход ведущего ждёт модель: даём до 45 с вместо общих 15. */
const TURN_TIMEOUT = 45_000;

// `turn` — сколько ответов было засчитано, когда ученик отвечал (`run.turn`). Повтор после обрыва связи с тем же
// номером сервер не засчитает второй раз: 409 STORY_TURN_STALE (2026-10-30).
export async function sendTextTurn(runId: string, text: string, turn: number): Promise<TurnResponse> {
  const { data } = await api.post<TurnResponse>(`/stories/runs/${runId}/turns`, { text, turn }, { timeout: TURN_TIMEOUT });
  return data;
}

function voiceForm(audio: Blob, turn: number): FormData {
  const form = new FormData();
  const ext = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm';
  form.append('audio', audio, `reply.${ext}`);
  form.append('turn', String(turn));
  return form;
}

export async function sendVoiceTurn(runId: string, audio: Blob, turn: number): Promise<TurnResponse> {
  const form = voiceForm(audio, turn);
  const { data } = await api.post<TurnResponse>(`/stories/runs/${runId}/voice`, form, { timeout: TURN_TIMEOUT + 20_000 });
  return data;
}

/** Итог POST /cards/story-words: модуль слов из игры и сколько слов легло. */
export interface StoryWordsSaved {
  module_id: string;
  module_name: string;
  saved: number;
  skipped: number;
  failed: number;
}

/** Слова из игры — в модуль одним запросом, и без подтверждённой почты (2026-10-30). Сервер по каждому слову обогащает
 *  карточку словарём — до 20 слов это долго, ждём дольше обычного; повтор безопасен (без дублей). */
export async function postStoryWords(words: StoryWord[], moduleName: string): Promise<StoryWordsSaved> {
  const { data } = await api.post<StoryWordsSaved>(
    '/cards/story-words',
    { module_name: moduleName, words: words.map((w) => ({ en: w.en, ru: w.ru })) },
    { timeout: 120_000 },
  );
  return data;
}

// ------------------------------------------------------------------ демо без входа (2026-10-25)
export async function fetchDemoInfo(): Promise<DemoInfo> {
  const { data } = await api.get<DemoInfo>('/stories/demo');
  return data;
}

export async function startDemo(level?: LearnerLevel): Promise<StoryRun> {
  const { data } = await api.post<StoryRun>('/stories/demo', level ? { level } : undefined);
  return data;
}

export async function fetchDemo(demoId: string): Promise<StoryRun> {
  const { data } = await api.get<StoryRun>(`/stories/demo/${demoId}`);
  return data;
}

export async function sendDemoTextTurn(demoId: string, text: string, turn: number): Promise<TurnResponse> {
  const { data } = await api.post<TurnResponse>(`/stories/demo/${demoId}/turn`, { text, turn }, { timeout: TURN_TIMEOUT });
  return data;
}

export async function sendDemoVoiceTurn(demoId: string, audio: Blob, turn: number): Promise<TurnResponse> {
  const form = voiceForm(audio, turn);
  const { data } = await api.post<TurnResponse>(`/stories/demo/${demoId}/voice`, form, { timeout: TURN_TIMEOUT + 20_000 });
  return data;
}

export async function abandonRun(runId: string): Promise<void> {
  await api.post(`/stories/runs/${runId}/abandon`);
}

export async function fetchStoryMistakes(): Promise<StoryMistake[]> {
  const { data } = await api.get<{ items: StoryMistake[] }>('/stories/mistakes');
  return data.items ?? [];
}

export async function sendChapterInterest(chapterId: string, choice: 'yes' | 'like'): Promise<void> {
  await api.post(`/stories/chapters/${chapterId}/interest`, { choice });
}
